// How long each part of a recipe can be, and the messages people see when something
// doesn't fit. The numbers match the API's database columns. The API keeps the same
// list in app/Libs/recipeLimits.js (morsel-web-app-server): change both together.
export const RECIPE_LIMITS = {
  name: { min: 2, max: 100 },
  description: { min: 2, max: 4096 },
  amount: { max: 100 }, // the "12" in "12 cookies"
  measurement: { max: 100 }, // the "cookies"
  ingredientQuantity: { max: 50 },
  ingredientMeasurement: { max: 100 },
  ingredientName: { max: 255 },
  step: { min: 2, max: 1024 },
  sourceName: { max: 255 },
  sourceUrl: { max: 2048 },
}

// Letters in any language (é, ñ...), numbers, spaces and everyday punctuation, so
// "Mac & Cheese", "Grandma's Pot-Roast" and "Crème brûlée" all work
const RECIPE_NAME_ALLOWED = /[\p{L}\p{M}\p{N} '’&\-.,!?()/:+#"“”]/gu

export const formatNumber = (n) => Number(n).toLocaleString('en-US')
export const lengthOf = (value) => String(value ?? '').trim().length

const characters = (n) => `${n} character${n === 1 ? '' : 's'}`
const isBlank = (value) => !String(value ?? '').trim()

const tooLong = (label, max, value) =>
  `${label} can be up to ${formatNumber(max)} characters. Yours is ${formatNumber(
    lengthOf(value)
  )}.`

// react-hook-form "validate" checks: each returns true, or the message to show
export const maxLengthCheck = (label, max) => (value) =>
  lengthOf(value) <= max || tooLong(label, max, value)

export const minLengthCheck = (message, min) => (value) =>
  isBlank(value) || lengthOf(value) >= min || message

export const requiredCheck =
  (message, isRequired = true) =>
  (value) =>
    !isRequired || !isBlank(value) || message

export const recipeNameCharactersCheck = (value) => {
  const bad = Array.from(new Set(String(value ?? '').replace(RECIPE_NAME_ALLOWED, '')))
  return (
    !bad.length ||
    `Recipe names can use letters, numbers, spaces and everyday punctuation. Please take out: ${bad.join(
      ' '
    )}`
  )
}

export const MESSAGES = {
  nameRequired: 'Please give your recipe a name.',
  nameMin: `Recipe names need at least ${characters(RECIPE_LIMITS.name.min)}.`,
  descriptionRequired: 'Please add a short summary of the recipe.',
  descriptionMin: `Please write a little more in the summary (at least ${characters(
    RECIPE_LIMITS.description.min
  )}).`,
  categoriesRequired: 'Please pick at least one category.',
  stepEmpty: (number) => `Step ${number} is empty. Write it out or remove it.`,
  stepMin: (number) => `Step ${number} needs at least ${characters(RECIPE_LIMITS.step.min)}.`,
  stepMax: (number) => (value) =>
    lengthOf(value) <= RECIPE_LIMITS.step.max ||
    `${tooLong(`Step ${number}`, RECIPE_LIMITS.step.max, value)} Try splitting it into two steps.`,
  photoTooBig: (bytes) =>
    `That photo is ${Math.ceil(bytes / 1048576)} MB. Please choose one under 15 MB.`,
  photoWrongType: 'Photos need to be a JPG or PNG picture. Please choose a different photo.',
  fileTooBig: 'That file is too big to upload. Please choose a smaller photo or recording.',
}

// Which page of the recipe form each API field is on
const FIELD_PAGES = {
  1: [
    'name',
    'description',
    'categories',
    'image',
    'card_images',
    'audio',
    'groups',
    'source_name',
    'source_url',
    'recipe_for', // "Whose recipe is this?"
    'credited_name',
  ],
  2: ['amount', 'measurement', 'ingredients'],
  3: ['steps'],
}
const PAGE_NAMES = { 1: 'Photo & details', 2: 'Ingredients', 3: 'Steps' }

const pageOf = (path) => {
  const top = String(path).split(/[.[]/)[0]
  return Number(Object.keys(FIELD_PAGES).find((page) => FIELD_PAGES[page].includes(top))) || null
}

/**
 * Show what the API refused. Fields on this page get their message next to the field;
 * anything else comes back as sentences for the box above the buttons. Returns those
 * sentences (empty when every problem is shown next to its field).
 */
export const showServerErrors = (error, setError, page) => {
  const response = error?.response
  if (!response) return [] // no connection: the app's "can't reach Morsel" screen shows
  const { status, data: body } = response
  const data = body?.data

  if (status === 413) return [MESSAGES.fileTooBig]

  if (status === 422 && data && typeof data === 'object') {
    const elsewhere = []
    Object.entries(data).forEach(([field, message]) => {
      // "steps[1].description" -> "steps.1.description", the form's own name for it
      const path = field.replace(/\[(\d+)\]/g, '.$1')
      const fieldPage = pageOf(path)
      if (fieldPage === page) {
        setError(path, { type: 'server', message: String(message) })
      } else {
        elsewhere.push(
          fieldPage ? `${message} (on the ${PAGE_NAMES[fieldPage]} page)` : String(message)
        )
      }
    })
    return elsewhere
  }

  if (status >= 500) return [] // the app's "something went wrong" screen shows
  if (typeof data === 'string' && data.trim() && !data.trim().startsWith('<')) {
    return [data.replace(/^(E_[A-Z_]+|\d+):\s*/, '')]
  }
  return ["We couldn't save that. Please check the form and try again."]
}

// Bring the first problem on the page into view, once it has been drawn
export const scrollToFirstProblem = () =>
  setTimeout(() => {
    const first = document.querySelector('.formFieldError, .formProblems')
    if (!first) return
    first.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const field = first.closest('[data-field]')?.querySelector('input, textarea')
    field?.focus({ preventScroll: true })
  }, 50)
